import React from 'react';
import { motion } from 'framer-motion';
import type { SceneSection } from '../manifest';
import type { StylePack } from '../style-packs';
import { PixarBlurText, PixarFloatingBadge } from '../interactive/PixarBlurText';

interface StorybookPresentationSectionProps {
  section: SceneSection;
  pack: StylePack;
  showCard: boolean;
  setShowCard: (show: boolean) => void;
}

const STORYBOOK_VOWS = [
  { step: 1, title: 'First Promise', desc: 'To share life’s adventures and cherish every sunrise together.' },
  { step: 2, title: 'Second Promise', desc: 'To stand as each other’s greatest strength in joy and sorrow.' },
  { step: 3, title: 'Third Promise', desc: 'To nurture our home with laughter, warmth, and lifelong friendship.' },
  { step: 4, title: 'Fourth Promise', desc: 'To respect our families and honor traditions with love.' },
  { step: 5, title: 'Fifth Promise', desc: 'To dream fearlessly and build our shared kingdom of hope.' },
  { step: 6, title: 'Sixth Promise', desc: 'To be patient, forgive easily, and grow wiser together each year.' },
  { step: 7, title: 'Seventh Promise', desc: 'To walk hand-in-hand forever, our fairy-tale without end.' },
];

export function StorybookPresentationSection({
  section,
  pack,
  showCard,
  setShowCard,
}: StorybookPresentationSectionProps) {
  const moment =
    section.momentId ??
    (section.order === 0
      ? 'engagement'
      : section.order === 1
      ? 'haldi'
      : section.order === 2
      ? 'antarpat'
      : section.order === 3
      ? 'varmala'
      : 'saptapadi');

  // Ultra-clear floating liquid glass — leaves 80%+ of the 3D animated video crystal visible
  const baseCardStyle: React.CSSProperties = {
    backgroundColor: 'rgba(15, 23, 42, 0.48)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: '1.5px solid rgba(251, 191, 36, 0.42)',
    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.75), inset 0 0 25px rgba(249, 115, 22, 0.15)',
    color: '#F8FAFC',
    transform: 'translateZ(0)',
    willChange: 'transform',
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: pack.fonts.display,
    color: '#FFFFFF',
    textShadow: '0 2px 20px rgba(0,0,0,0.9), 0 0 35px rgba(249, 115, 22, 0.6)',
  };

  const subtitleStyle: React.CSSProperties = {
    fontFamily: pack.fonts.script ?? pack.fonts.display,
    color: '#FDE68A',
    textShadow: '0 2px 14px rgba(0,0,0,0.9)',
  };

  const pillBadgeStyle =
    'bg-slate-900/60 border border-amber-400/40 text-amber-200 font-sans backdrop-blur-xl shadow-[0_4px_16px_rgba(249,115,22,0.25)]';

  const toggleButton = (
    <button
      onClick={() => setShowCard(!showCard)}
      className="absolute -top-3.5 right-4 z-40 flex items-center gap-1.5 rounded-full border border-amber-300/50 bg-slate-900/90 px-3.5 py-1 text-[10px] font-black uppercase tracking-wider text-amber-300 shadow-xl backdrop-blur-md transition-all hover:scale-105 active:scale-95 cursor-pointer pointer-events-auto"
      title="Toggle pure 3D animation video showcase"
    >
      <span>{showCard ? '👁️ VIEW 3D VIDEO' : '✨ STORY DETAILS'}</span>
    </button>
  );

  return (
    <div className="relative z-10 w-full min-h-screen flex pointer-events-none">
      <style>{`
        @keyframes pixar-lens-bloom {
          0%, 100% { filter: drop-shadow(0 0 8px rgba(249, 115, 22, 0.4)); }
          50% { filter: drop-shadow(0 0 24px rgba(251, 191, 36, 0.85)); }
        }
        @keyframes pixar-specular-sweep {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .pixar-title-specular {
          background: linear-gradient(105deg, #FFFFFF 0%, #FFFBEB 25%, #FDE047 50%, #FB923C 75%, #FFFFFF 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: pixar-specular-sweep 4.5s ease-in-out infinite;
        }
      `}</style>

      {/* ───────────────────────────────────────────────────────────────────────────
          ACT I: SAGAI — Floating Glass Lens HUD on Right (Video Unblocked) with Rack-Focus Text Blur
         ─────────────────────────────────────────────────────────────────────────── */}
      {moment === 'engagement' && (
        <div className="w-full max-w-7xl mx-auto flex items-center justify-end px-6 md:px-14 py-12">
          <div
            className={`relative max-w-md md:max-w-lg w-full pointer-events-auto transition-opacity duration-300 ${
              showCard ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {toggleButton}

            {/* Pixar Luminous Storybook Chapter Marker — Racks into focus */}
            <div className="mb-3">
              <PixarFloatingBadge delay={0.05} icon="✨">
                CHAPTER I · THE PROMISE
              </PixarFloatingBadge>
            </div>

            <div style={baseCardStyle} className="p-7 md:p-9 rounded-3xl relative">
              {/* Rack-Focus Optical Blur Ceremony Title */}
              <PixarBlurText
                as="h2"
                delay={0.15}
                duration={1.0}
                className="text-2xl md:text-4xl font-extrabold leading-tight mb-2 pixar-title-specular"
                style={titleStyle}
              >
                {section.content.title}
              </PixarBlurText>

              {section.content.subtitle && (
                <PixarBlurText
                  as="p"
                  delay={0.3}
                  duration={0.9}
                  className="text-lg md:text-2xl mb-6 font-semibold"
                  style={subtitleStyle}
                >
                  {section.content.subtitle}
                </PixarBlurText>
              )}

              {/* Floating Bokeh Pills */}
              <div className="mt-4 flex flex-col gap-3">
                <motion.div
                  initial={{ filter: 'blur(16px)', opacity: 0, scale: 0.94 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.75, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  className={`flex items-center gap-3.5 px-4 py-3 rounded-2xl ${pillBadgeStyle}`}
                >
                  <span className="text-xl">💍</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Auspicious Moment</p>
                    <p className="text-xs md:text-sm font-bold text-white">{section.content.date}</p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ filter: 'blur(16px)', opacity: 0, scale: 0.94 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.75, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
                  className={`flex items-center gap-3.5 px-4 py-3 rounded-2xl ${pillBadgeStyle}`}
                >
                  <span className="text-xl">⏰</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Ring Exchange Mahurat</p>
                    <p className="text-xs md:text-sm font-bold text-white">{section.content.time || '05:00 PM Golden Hour'}</p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ filter: 'blur(16px)', opacity: 0, scale: 0.94 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.75, delay: 0.65, ease: [0.16, 1, 0.3, 1] }}
                  className={`flex items-center gap-3.5 px-4 py-3 rounded-2xl ${pillBadgeStyle}`}
                >
                  <span className="text-xl">🏰</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Fairytale Courtyard</p>
                    <p className="text-xs md:text-sm font-bold text-white">{section.content.venue?.name || 'Grand Palace Courtyard'}</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────────
          ACT II: HALDI — Translucent Lower-Third Ribbon with Liquid Sunlight Blur Reveal
         ─────────────────────────────────────────────────────────────────────────── */}
      {moment === 'haldi' && (
        <div className="w-full flex items-end justify-center pb-8 md:pb-12 px-4 md:px-10">
          <div
            className={`relative max-w-5xl w-full pointer-events-auto transition-opacity duration-300 ${
              showCard ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {toggleButton}

            <div
              style={baseCardStyle}
              className="p-6 md:p-8 rounded-3xl grid grid-cols-1 md:grid-cols-3 gap-6 items-center shadow-2xl relative overflow-hidden"
            >
              {/* Animated Warm Golden Sunburst Accent */}
              <div className="absolute -top-16 -left-16 w-44 h-44 rounded-full bg-amber-500/20 blur-3xl pointer-events-none" />

              {/* Column 1: Title & Theme with Rack Focus */}
              <div className="md:border-r border-amber-500/30 md:pr-6">
                <PixarFloatingBadge delay={0.05} icon="☀️" className="mb-2">
                  CHAPTER II · SUNLIT JOY
                </PixarFloatingBadge>
                <PixarBlurText
                  as="h2"
                  delay={0.15}
                  duration={0.95}
                  className="text-2xl md:text-3xl font-extrabold leading-tight pixar-title-specular"
                  style={titleStyle}
                >
                  {section.content.title}
                </PixarBlurText>
                {section.content.subtitle && (
                  <PixarBlurText
                    as="p"
                    delay={0.3}
                    duration={0.85}
                    className="text-base md:text-lg mt-1 font-semibold"
                    style={subtitleStyle}
                  >
                    {section.content.subtitle}
                  </PixarBlurText>
                )}
              </div>

              {/* Column 2: Date & Time */}
              <div className="space-y-3">
                <motion.div
                  initial={{ filter: 'blur(16px)', opacity: 0, scale: 0.94 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className={`flex items-center gap-3 p-3.5 rounded-2xl ${pillBadgeStyle}`}
                >
                  <span className="text-lg">💛</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-amber-300 font-bold">Turmeric Mahurat</p>
                    <p className="text-xs md:text-sm font-bold text-white">{section.content.date}</p>
                    <p className="text-[11px] text-amber-200 font-medium">{section.content.time || '10:30 AM Morning Sunlight'}</p>
                  </div>
                </motion.div>
              </div>

              {/* Column 3: Venue & Dress Code */}
              <div className="space-y-3">
                <motion.div
                  initial={{ filter: 'blur(16px)', opacity: 0, scale: 0.94 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  className={`flex items-center gap-3 p-3.5 rounded-2xl ${pillBadgeStyle}`}
                >
                  <span className="text-lg">🌻</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-amber-300 font-bold">Haldi Pavilion</p>
                    <p className="text-xs md:text-sm font-bold text-white line-clamp-1">{section.content.venue?.name || 'Sunny Marigold Garden'}</p>
                    <p className="text-[11px] text-amber-200 font-medium">Dress: Shades of Yellow & Gold</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────────
          ACT III: ANTARPAT — Center Floating Lens Pod with Defocus Bloom
         ─────────────────────────────────────────────────────────────────────────── */}
      {moment === 'antarpat' && (
        <div className="w-full flex items-center justify-center px-6 py-12">
          <div
            className={`relative max-w-2xl w-full pointer-events-auto transition-opacity duration-300 text-center ${
              showCard ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {toggleButton}

            <div style={baseCardStyle} className="p-8 md:p-11 rounded-3xl relative">
              <div className="mb-3">
                <PixarFloatingBadge delay={0.05} icon="✦">
                  CHAPTER III · A SACRED GLIMPSE
                </PixarFloatingBadge>
              </div>

              <PixarBlurText
                as="h2"
                delay={0.15}
                duration={1.05}
                className="text-3xl md:text-5xl font-extrabold leading-tight mb-3 pixar-title-specular"
                style={titleStyle}
              >
                {section.content.title}
              </PixarBlurText>

              {section.content.subtitle && (
                <PixarBlurText
                  as="p"
                  delay={0.3}
                  duration={0.9}
                  className="text-xl md:text-2xl mb-6 font-semibold"
                  style={subtitleStyle}
                >
                  {section.content.subtitle}
                </PixarBlurText>
              )}

              <PixarBlurText
                as="p"
                delay={0.45}
                duration={0.8}
                className="text-xs md:text-sm text-slate-200/90 max-w-lg mx-auto mb-6 leading-relaxed"
              >
                As the embroidered sacred silk veil lowers, two souls catch their first joyful glimpse of eternity under the palace chandelier.
              </PixarBlurText>

              <div className="flex flex-wrap items-center justify-center gap-3">
                <motion.span
                  initial={{ filter: 'blur(14px)', opacity: 0 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.6, delay: 0.55 }}
                  className={`px-4 py-2 rounded-xl text-xs md:text-sm font-bold ${pillBadgeStyle}`}
                >
                  📅 {section.content.date}
                </motion.span>
                <motion.span
                  initial={{ filter: 'blur(14px)', opacity: 0 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.6, delay: 0.65 }}
                  className={`px-4 py-2 rounded-xl text-xs md:text-sm font-bold ${pillBadgeStyle}`}
                >
                  ⏰ {section.content.time || '06:30 PM Sunset Mahurat'}
                </motion.span>
                <motion.span
                  initial={{ filter: 'blur(14px)', opacity: 0 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.6, delay: 0.75 }}
                  className={`px-4 py-2 rounded-xl text-xs md:text-sm font-bold ${pillBadgeStyle}`}
                >
                  📍 {section.content.venue?.name || 'Central Palace Mandap'}
                </motion.span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────────
          ACT IV: VARMALA — Left-Anchored Star-Lantern HUD with Optical Lens Blur
         ─────────────────────────────────────────────────────────────────────────── */}
      {moment === 'varmala' && (
        <div className="w-full max-w-7xl mx-auto flex items-center justify-start px-6 md:px-14 py-12">
          <div
            className={`relative max-w-md md:max-w-lg w-full pointer-events-auto transition-opacity duration-300 ${
              showCard ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {toggleButton}

            <div className="mb-3">
              <PixarFloatingBadge delay={0.05} icon="🌸">
                CHAPTER IV · STARLIT SKY
              </PixarFloatingBadge>
            </div>

            <div style={baseCardStyle} className="p-7 md:p-9 rounded-3xl relative">
              <PixarBlurText
                as="h2"
                delay={0.15}
                duration={1.0}
                className="text-2xl md:text-4xl font-extrabold leading-tight mb-2 pixar-title-specular"
                style={titleStyle}
              >
                {section.content.title}
              </PixarBlurText>

              {section.content.subtitle && (
                <PixarBlurText
                  as="p"
                  delay={0.3}
                  duration={0.9}
                  className="text-lg md:text-2xl mb-6 font-semibold"
                  style={subtitleStyle}
                >
                  {section.content.subtitle}
                </PixarBlurText>
              )}

              <div className="mt-4 flex flex-col gap-3">
                <motion.div
                  initial={{ filter: 'blur(16px)', opacity: 0, scale: 0.94 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, delay: 0.45 }}
                  className={`flex items-center gap-3.5 px-4 py-3 rounded-2xl ${pillBadgeStyle}`}
                >
                  <span className="text-xl">🌺</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Garland Exchange</p>
                    <p className="text-xs md:text-sm font-bold text-white">{section.content.date}</p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ filter: 'blur(16px)', opacity: 0, scale: 0.94 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, delay: 0.55 }}
                  className={`flex items-center gap-3.5 px-4 py-3 rounded-2xl ${pillBadgeStyle}`}
                >
                  <span className="text-xl">✨</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Starlight Mahurat</p>
                    <p className="text-xs md:text-sm font-bold text-white">{section.content.time || '07:30 PM Fireworks'}</p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ filter: 'blur(16px)', opacity: 0, scale: 0.94 }}
                  whileInView={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, delay: 0.65 }}
                  className={`flex items-center gap-3.5 px-4 py-3 rounded-2xl ${pillBadgeStyle}`}
                >
                  <span className="text-xl">🎆</span>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Sky Terrace</p>
                    <p className="text-xs md:text-sm font-bold text-white">{section.content.venue?.name || 'Grand Palace Terrace'}</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────────
          ACT V: SAAT PHERE — Bottom Floating Vow Ribbon with Sequential Rack-Focus Unblur
         ─────────────────────────────────────────────────────────────────────────── */}
      {moment === 'saptapadi' && (
        <div className="w-full flex items-end justify-center pb-6 md:pb-10 px-4 md:px-8">
          <div
            className={`relative max-w-6xl w-full pointer-events-auto transition-opacity duration-300 ${
              showCard ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {toggleButton}

            <div style={baseCardStyle} className="p-6 md:p-8 rounded-3xl shadow-2xl">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-4 pb-3 border-b border-amber-500/25">
                <div>
                  <div className="mb-1">
                    <PixarFloatingBadge delay={0.05} icon="🔥">
                      FINAL CHAPTER · FOREVER & ALWAYS
                    </PixarFloatingBadge>
                  </div>
                  <PixarBlurText
                    as="h2"
                    delay={0.15}
                    duration={1.0}
                    className="text-2xl md:text-3xl font-extrabold pixar-title-specular"
                    style={titleStyle}
                  >
                    {section.content.title}
                  </PixarBlurText>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1.5 rounded-xl text-xs font-bold ${pillBadgeStyle}`}>
                    📅 {section.content.date}
                  </span>
                  <span className={`px-3 py-1.5 rounded-xl text-xs font-bold ${pillBadgeStyle}`}>
                    ⏰ {section.content.time || '08:30 PM Phere'}
                  </span>
                </div>
              </div>

              {/* 7 Vows Floating Crystal Badges with Sequential Rack-Focus Unblur */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2.5">
                {STORYBOOK_VOWS.map((vow, i) => (
                  <motion.div
                    key={vow.step}
                    initial={{ filter: 'blur(18px) brightness(2)', opacity: 0, scale: 0.9 }}
                    whileInView={{ filter: 'blur(0px) brightness(1)', opacity: 1, scale: 1 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.65, delay: 0.2 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                    className="p-3 rounded-2xl bg-slate-900/80 border border-amber-400/40 text-left backdrop-blur-xl shadow-lg hover:border-amber-400/80 hover:scale-105 transition-all"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-black text-amber-300">VOW {vow.step}</span>
                      <span className="text-xs">✨</span>
                    </div>
                    <p className="text-[11px] font-bold text-white line-clamp-1">{vow.title}</p>
                    <p className="text-[9px] text-slate-300 mt-0.5 line-clamp-2 leading-relaxed">{vow.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
